import { type Ticket, type TicketActivity, type TicketAttachment } from "@entities/ticket";
import { type TicketQueue, type TicketQueueUser, type TicketQueueTicket } from "@entities/ticket";
import {
  type IssueCategory,
  type IssueCategoryQueue,
  type IssueCategoryAssetModel,
} from "@entities/ticket";

const getDaysFromNow = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

const getHoursAgo = (hours: number): string => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date.toISOString();
};

export const mockTickets: Ticket[] = [
  {
    id: "ticket-1",
    account_id: "1",
    branch_id: "branch-1",
    asset_id: "asset-1",
    problem_category_id: "icat-1",
    queue_id: "queue-2",
    title: "Backofen erreicht nicht volle Temperatur",
    description:
      "Der Etagenofen in der Backstube erreicht nur noch 220°C statt 280°C. Oberhitze scheint defekt.",
    priority: "HIGH",
    status: "OPEN",
    assigned_to_user_id: "user-admin-1",
    created_by_user_id: "per-3",
    last_updated_by_user_id: "per-6",
    createdAt: getHoursAgo(48),
    updatedAt: getHoursAgo(24),
  },
  {
    id: "ticket-2",
    account_id: "1",
    branch_id: "branch-2",
    asset_id: "asset-5",
    problem_category_id: "icat-1",
    queue_id: "queue-1",
    title: "Kühltheke-Beleuchtung flackert",
    description: "LED-Beleuchtung in der Kühltheke flackert seit gestern.",
    priority: "LOW",
    status: "SCHEDULED",
    scheduled_date: getDaysFromNow(2),
    assigned_to_user_id: "per-7",
    created_by_user_id: "per-8",
    last_updated_by_user_id: "per-7",
    createdAt: getHoursAgo(72),
    updatedAt: getHoursAgo(12),
  },
  {
    id: "ticket-3",
    account_id: "1",
    branch_id: "branch-2",
    asset_id: "asset-6",
    problem_category_id: "icat-1",
    queue_id: "queue-4",
    title: "Kassensystem druckt nicht",
    description: "Bondrucker der Kasse druckt keine Belege mehr.",
    priority: "CRITICAL",
    status: "OPEN",
    assigned_to_user_id: "per-16",
    created_by_user_id: "per-11",
    last_updated_by_user_id: "per-11",
    createdAt: getHoursAgo(4),
    updatedAt: getHoursAgo(4),
  },
  {
    id: "ticket-4",
    account_id: "1",
    branch_id: "branch-1",
    asset_id: "asset-4",
    problem_category_id: "icat-1",
    queue_id: "queue-2",
    title: "Konvektomat Dampf funktioniert nicht",
    description: "Dampffunktion des Konvektomaten ist ausgefallen.",
    priority: "HIGH",
    status: "OPEN",
    assigned_to_user_id: "user-admin-1",
    created_by_user_id: "per-4",
    last_updated_by_user_id: "per-6",
    createdAt: getHoursAgo(36),
    updatedAt: getHoursAgo(24),
  },
  {
    id: "ticket-5",
    account_id: "1",
    branch_id: "branch-1",
    asset_id: "asset-8",
    problem_category_id: "icat-1",
    queue_id: "queue-5",
    title: "Spülmaschine Fehlermeldung E42",
    description: "Gewerbespülmaschine zeigt Fehler E42.",
    priority: "HIGH",
    status: "WAITING_FOR_SPARE_PARTS",
    waiting_spare_parts_date: getDaysFromNow(-2),
    assigned_to_user_id: "per-17",
    created_by_user_id: "per-5",
    last_updated_by_user_id: "per-17",
    createdAt: getHoursAgo(24),
    updatedAt: getHoursAgo(8),
  },
  {
    id: "ticket-6",
    account_id: "1",
    branch_id: "branch-3",
    asset_id: "asset-9",
    problem_category_id: "icat-1",
    queue_id: "queue-3",
    title: "Tiefkühlschrank Temperatur zu hoch",
    description: "Tiefkühlschrank hält nur noch -12°C statt -18°C.",
    priority: "CRITICAL",
    status: "OPEN",
    assigned_to_user_id: "per-15",
    created_by_user_id: "per-9",
    last_updated_by_user_id: "per-7",
    createdAt: getHoursAgo(2),
    updatedAt: getHoursAgo(1),
  },
  {
    id: "ticket-7",
    account_id: "1",
    branch_id: "branch-3",
    asset_id: "asset-11",
    problem_category_id: "icat-1",
    queue_id: "queue-3",
    title: "Tiefkühler Totalausfall - DRINGEND",
    description:
      "Tiefkühler komplett ausgefallen. Ware droht zu verderben. Sofortiger Einsatz erforderlich!",
    priority: "CRITICAL",
    status: "OPEN",
    assigned_to_user_id: "per-15",
    created_by_user_id: "per-9",
    last_updated_by_user_id: "per-9",
    createdAt: getHoursAgo(1),
    updatedAt: getHoursAgo(1),
  },
  {
    id: "ticket-8",
    account_id: "1",
    branch_id: "branch-4",
    asset_id: "asset-13",
    problem_category_id: "icat-1",
    queue_id: "queue-3",
    title: "Kühltheke Kompressor defekt",
    description: "Kompressor macht laute Geräusche und Kühlleistung sinkt. Lebensmittel gefährdet.",
    priority: "CRITICAL",
    status: "OPEN",
    assigned_to_user_id: "per-15",
    created_by_user_id: "per-10",
    last_updated_by_user_id: "per-10",
    createdAt: getHoursAgo(3),
    updatedAt: getHoursAgo(3),
  },
  {
    id: "ticket-9",
    account_id: "1",
    branch_id: "branch-5",
    asset_id: "asset-15",
    problem_category_id: "icat-1",
    title: "Kühltheke Temperatur schwankt",
    description: "Temperatur schwankt zwischen 2°C und 8°C. Thermostat prüfen.",
    priority: "HIGH",
    status: "OPEN",
    assigned_to_user_id: "user-admin-1",
    created_by_user_id: "per-11",
    last_updated_by_user_id: "per-11",
    createdAt: getHoursAgo(18),
    updatedAt: getHoursAgo(18),
  },
  {
    id: "ticket-10",
    account_id: "1",
    branch_id: "branch-5",
    asset_id: "asset-16",
    problem_category_id: "icat-1",
    queue_id: "queue-4",
    title: "Kasse reagiert langsam",
    description: "Kassensystem reagiert verzögert auf Eingaben. Kunden beschweren sich.",
    priority: "MEDIUM",
    status: "OPEN",
    assigned_to_user_id: "per-16",
    created_by_user_id: "per-11",
    last_updated_by_user_id: "per-11",
    createdAt: getHoursAgo(48),
    updatedAt: getHoursAgo(48),
  },
  {
    id: "ticket-11",
    account_id: "1",
    branch_id: "branch-6",
    asset_id: "asset-17",
    problem_category_id: "icat-1",
    queue_id: "queue-3",
    title: "Kühltheke komplett ausgefallen",
    description: "Kühltheke zeigt keine Funktion mehr. Notkühlung mit Eis eingerichtet.",
    priority: "CRITICAL",
    status: "OPEN",
    assigned_to_user_id: "per-15",
    created_by_user_id: "per-12",
    last_updated_by_user_id: "per-12",
    createdAt: getHoursAgo(5),
    updatedAt: getHoursAgo(5),
  },
  {
    id: "ticket-12",
    account_id: "1",
    branch_id: "branch-6",
    asset_id: "asset-18",
    problem_category_id: "icat-1",
    queue_id: "queue-4",
    title: "Kassendrucker Papierstau",
    description: "Bondrucker hat häufig Papierstau. Druckmechanismus prüfen.",
    priority: "HIGH",
    status: "OPEN",
    assigned_to_user_id: "user-admin-1",
    created_by_user_id: "per-12",
    last_updated_by_user_id: "per-12",
    createdAt: getHoursAgo(24),
    updatedAt: getHoursAgo(24),
  },
  {
    id: "ticket-13",
    account_id: "1",
    branch_id: "branch-7",
    asset_id: "asset-19",
    problem_category_id: "icat-1",
    title: "Kühltheke Beleuchtung defekt",
    description: "LED-Beleuchtung in der Kühltheke funktioniert nicht mehr.",
    priority: "HIGH",
    status: "CLOSED",
    created_by_user_id: "per-13",
    last_updated_by_user_id: "per-7",
    createdAt: getHoursAgo(96),
    updatedAt: getHoursAgo(12),
  },
  {
    id: "ticket-14",
    account_id: "1",
    branch_id: "branch-7",
    asset_id: "asset-20",
    problem_category_id: "icat-1",
    queue_id: "queue-4",
    title: "Kassenbildschirm flackert",
    description: "Bildschirm der Kasse flackert gelegentlich. Noch funktionsfähig.",
    priority: "LOW",
    status: "QUESTION",
    assigned_to_user_id: "per-16",
    created_by_user_id: "per-13",
    last_updated_by_user_id: "per-16",
    createdAt: getHoursAgo(120),
    updatedAt: getHoursAgo(48),
  },
  {
    id: "ticket-15",
    account_id: "1",
    branch_id: "branch-1",
    asset_id: "asset-2",
    problem_category_id: "icat-1",
    queue_id: "queue-2",
    title: "Etagenofen Tür klemmt",
    description: "Die Ofentür des unteren Etagenofen klemmt beim Öffnen.",
    priority: "HIGH",
    status: "CLOSED",
    assigned_to_user_id: "user-admin-1",
    created_by_user_id: "per-6",
    last_updated_by_user_id: "per-14",
    createdAt: getHoursAgo(168),
    updatedAt: getHoursAgo(24),
  },
];

export const mockTicketActivities: TicketActivity[] = [
  {
    id: "activity-1",
    account_id: "1",
    ticket_id: "ticket-1",
    created_by_user_id: "per-3",
    type: "SYSTEM",
    visibility: "PUBLIC",
    message: "Ticket erstellt",
    createdAt: getHoursAgo(48),
  },
  {
    id: "activity-2",
    account_id: "1",
    ticket_id: "ticket-1",
    created_by_user_id: "per-6",
    type: "ASSIGNMENT",
    visibility: "PUBLIC",
    message: "Ticket zugewiesen",
    meta: { to: "per-14" },
    createdAt: getHoursAgo(47),
  },
  {
    id: "activity-3",
    account_id: "1",
    ticket_id: "ticket-1",
    created_by_user_id: "per-14",
    type: "COMMENT",
    visibility: "PUBLIC",
    message: "Ich schaue mir das morgen früh an. Benötige ggf. Ersatzteil für Oberhitze.",
    createdAt: getHoursAgo(40),
  },
  {
    id: "activity-4",
    account_id: "1",
    ticket_id: "ticket-1",
    created_by_user_id: "per-6",
    type: "COMMENT",
    visibility: "INTERNAL",
    message: "Lieferant hat Ersatzteil auf Lager. Lieferzeit 2-3 Tage.",
    createdAt: getHoursAgo(36),
  },
  {
    id: "activity-10",
    account_id: "1",
    ticket_id: "ticket-2",
    created_by_user_id: "per-8",
    type: "SYSTEM",
    visibility: "PUBLIC",
    message: "Ticket erstellt",
    createdAt: getHoursAgo(72),
  },
  {
    id: "activity-11",
    account_id: "1",
    ticket_id: "ticket-2",
    created_by_user_id: "per-7",
    type: "STATUS_CHANGE",
    visibility: "PUBLIC",
    message: "Status geändert",
    meta: { from: "OPEN", to: "SCHEDULED" },
    createdAt: getHoursAgo(48),
  },
  {
    id: "activity-12",
    account_id: "1",
    ticket_id: "ticket-2",
    created_by_user_id: "per-7",
    type: "COMMENT",
    visibility: "PUBLIC",
    message: "LED-Treiber defekt. Neuen bestellt.",
    createdAt: getHoursAgo(24),
  },
  {
    id: "activity-20",
    account_id: "1",
    ticket_id: "ticket-3",
    created_by_user_id: "per-11",
    type: "SYSTEM",
    visibility: "PUBLIC",
    message: "Ticket erstellt",
    createdAt: getHoursAgo(4),
  },
  {
    id: "activity-21",
    account_id: "1",
    ticket_id: "ticket-3",
    created_by_user_id: "per-16",
    type: "COMMENT",
    visibility: "PUBLIC",
    message: "Bin in 30 Minuten vor Ort.",
    createdAt: getHoursAgo(3),
  },
  {
    id: "activity-30",
    account_id: "1",
    ticket_id: "ticket-13",
    created_by_user_id: "per-13",
    type: "SYSTEM",
    visibility: "PUBLIC",
    message: "Ticket erstellt",
    createdAt: getHoursAgo(96),
  },
  {
    id: "activity-31",
    account_id: "1",
    ticket_id: "ticket-13",
    created_by_user_id: "per-7",
    type: "STATUS_CHANGE",
    visibility: "PUBLIC",
    message: "Status geändert",
    meta: { from: "OPEN", to: "SCHEDULED" },
    createdAt: getHoursAgo(72),
  },
  {
    id: "activity-32",
    account_id: "1",
    ticket_id: "ticket-13",
    created_by_user_id: "per-7",
    type: "COMMENT",
    visibility: "PUBLIC",
    message: "LED-Streifen ausgetauscht. Funktioniert wieder.",
    createdAt: getHoursAgo(24),
  },
  {
    id: "activity-33",
    account_id: "1",
    ticket_id: "ticket-13",
    created_by_user_id: "per-7",
    type: "STATUS_CHANGE",
    visibility: "PUBLIC",
    message: "Als gelöst markiert",
    meta: { from: "SCHEDULED", to: "CLOSED" },
    createdAt: getHoursAgo(12),
  },
  {
    id: "activity-40",
    account_id: "1",
    ticket_id: "ticket-15",
    created_by_user_id: "per-6",
    type: "SYSTEM",
    visibility: "PUBLIC",
    message: "Ticket erstellt",
    createdAt: getHoursAgo(168),
  },
  {
    id: "activity-41",
    account_id: "1",
    ticket_id: "ticket-15",
    created_by_user_id: "per-14",
    type: "STATUS_CHANGE",
    visibility: "PUBLIC",
    message: "Status geändert",
    meta: { from: "OPEN", to: "SCHEDULED" },
    createdAt: getHoursAgo(144),
  },
  {
    id: "activity-42",
    account_id: "1",
    ticket_id: "ticket-15",
    created_by_user_id: "per-14",
    type: "COMMENT",
    visibility: "PUBLIC",
    message: "Scharniere justiert und geschmiert.",
    createdAt: getHoursAgo(48),
  },
  {
    id: "activity-43",
    account_id: "1",
    ticket_id: "ticket-15",
    created_by_user_id: "per-14",
    type: "STATUS_CHANGE",
    visibility: "PUBLIC",
    message: "Als gelöst markiert",
    meta: { from: "SCHEDULED", to: "CLOSED" },
    createdAt: getHoursAgo(36),
  },
  {
    id: "activity-44",
    account_id: "1",
    ticket_id: "ticket-15",
    created_by_user_id: "per-6",
    type: "STATUS_CHANGE",
    visibility: "PUBLIC",
    message: "Ticket geschlossen",
    meta: { from: "QUESTION", to: "CLOSED" },
    createdAt: getHoursAgo(24),
  },
];

export const mockTicketAttachments: TicketAttachment[] = [
  {
    id: "attach-1",
    account_id: "1",
    ticket_id: "ticket-1",
    uploaded_by_user_id: "per-3",
    file_url: "/placeholder.svg",
    file_name: "ofen-temperatur.jpg",
    mime_type: "image/jpeg",
    createdAt: getHoursAgo(48),
  },
  {
    id: "attach-2",
    account_id: "1",
    ticket_id: "ticket-3",
    uploaded_by_user_id: "per-11",
    file_url: "/placeholder.svg",
    file_name: "drucker-fehler.jpg",
    mime_type: "image/jpeg",
    createdAt: getHoursAgo(4),
  },
];

export const mockTicketQueues: TicketQueue[] = [
  {
    id: "queue-1",
    tenant_id: "1",
    name: "Technik Intern",
    description: "Interne Techniker für erste Fehleranalyse und einfache Reparaturen",
    email_new_ticket:
      "<p>Neues Ticket erstellt: {{title}}</p><p>Bitte schnellstmöglich bearbeiten.</p>",
    email_signature: "<p>Mit freundlichen Grüßen,<br/>Technik-Team Bäckerei Rhein-Neckar</p>",
    is_default: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "queue-2",
    tenant_id: "1",
    name: "Backofenservice",
    description: "Externe Servicetechniker für Backöfen (Wiesheu, Debag, Miwe)",
    email_new_ticket: "<p>Serviceauftrag Backofen: {{title}}</p>",
    email_signature: "<p>Bäckerei Rhein-Neckar GmbH</p>",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "queue-3",
    tenant_id: "1",
    name: "Kältetechnik",
    description: "Servicetechniker für Kühlgeräte und Tiefkühler",
    email_new_ticket: "<p>Kältetechnik-Einsatz erforderlich: {{title}}</p>",
    email_signature: "<p>Bäckerei Rhein-Neckar GmbH</p>",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "queue-4",
    tenant_id: "1",
    name: "Kassensysteme",
    description: "Vectron Support für Kassenprobleme",
    email_new_ticket: "<p>Kassenproblem: {{title}}</p>",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "queue-5",
    tenant_id: "1",
    name: "Spültechnik",
    description: "Winterhalter Service für Spülmaschinen",
    email_new_ticket: "<p>Spülmaschinen-Service: {{title}}</p>",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const mockTicketQueueUsers: TicketQueueUser[] = [
  {
    id: "quser-1",
    queue_id: "queue-1",
    user_id: "demo-user-3",
    createdAt: new Date().toISOString(),
  },
  {
    id: "quser-2",
    queue_id: "queue-1",
    user_id: "demo-user-4",
    createdAt: new Date().toISOString(),
  },
  {
    id: "quser-3",
    queue_id: "queue-2",
    user_id: "demo-user-3",
    createdAt: new Date().toISOString(),
  },
  {
    id: "quser-4",
    queue_id: "queue-3",
    user_id: "demo-user-4",
    createdAt: new Date().toISOString(),
  },
  {
    id: "quser-5",
    queue_id: "queue-5",
    user_id: "demo-user-3",
    createdAt: new Date().toISOString(),
  },
];

export const mockTicketQueueTickets: TicketQueueTicket[] = [
  { id: "qtkt-1", queue_id: "queue-1", ticket_id: "ticket-1", createdAt: new Date().toISOString() },
  { id: "qtkt-2", queue_id: "queue-2", ticket_id: "ticket-1", createdAt: new Date().toISOString() },
  { id: "qtkt-3", queue_id: "queue-1", ticket_id: "ticket-2", createdAt: new Date().toISOString() },
  { id: "qtkt-4", queue_id: "queue-3", ticket_id: "ticket-6", createdAt: new Date().toISOString() },
  { id: "qtkt-5", queue_id: "queue-4", ticket_id: "ticket-3", createdAt: new Date().toISOString() },
  { id: "qtkt-6", queue_id: "queue-5", ticket_id: "ticket-5", createdAt: new Date().toISOString() },
];

export const mockIssueCategories: IssueCategory[] = [
  {
    id: "icat-1",
    tenant_id: "1",
    name: "Störung",
    description: "Gerät funktioniert nicht oder nur eingeschränkt",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icat-2",
    tenant_id: "1",
    name: "Wartungsanfrage",
    description: "Anfragen für planmäßige Wartung",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icat-3",
    tenant_id: "1",
    name: "Ersatzteilbestellung",
    description: "Bestellung von Ersatzteilen",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icat-4",
    tenant_id: "1",
    name: "Inspektion fällig",
    description: "Fällige Inspektionen melden",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icat-5",
    tenant_id: "1",
    name: "Hygienemangel",
    description: "Hygiene-relevante Probleme",
    createdAt: new Date().toISOString(),
  },
];

export const mockIssueCategoryQueues: IssueCategoryQueue[] = [
  { id: "icq-1", category_id: "icat-1", queue_id: "queue-1", createdAt: new Date().toISOString() },
  { id: "icq-2", category_id: "icat-1", queue_id: "queue-2", createdAt: new Date().toISOString() },
  { id: "icq-3", category_id: "icat-2", queue_id: "queue-1", createdAt: new Date().toISOString() },
  { id: "icq-4", category_id: "icat-3", queue_id: "queue-1", createdAt: new Date().toISOString() },
  { id: "icq-5", category_id: "icat-4", queue_id: "queue-1", createdAt: new Date().toISOString() },
  { id: "icq-6", category_id: "icat-5", queue_id: "queue-1", createdAt: new Date().toISOString() },
];

export const mockIssueCategoryAssetModels: IssueCategoryAssetModel[] = [
  {
    id: "icam-1",
    category_id: "icat-1",
    asset_model_id: "model-1",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-2",
    category_id: "icat-1",
    asset_model_id: "model-2",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-3",
    category_id: "icat-1",
    asset_model_id: "model-3",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-4",
    category_id: "icat-1",
    asset_model_id: "model-4",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-5",
    category_id: "icat-1",
    asset_model_id: "model-5",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-6",
    category_id: "icat-1",
    asset_model_id: "model-6",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-7",
    category_id: "icat-1",
    asset_model_id: "model-7",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-8",
    category_id: "icat-1",
    asset_model_id: "model-8",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-9",
    category_id: "icat-1",
    asset_model_id: "model-9",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-10",
    category_id: "icat-1",
    asset_model_id: "model-10",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-11",
    category_id: "icat-1",
    asset_model_id: "model-11",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-20",
    category_id: "icat-2",
    asset_model_id: "model-1",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-21",
    category_id: "icat-2",
    asset_model_id: "model-2",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-22",
    category_id: "icat-2",
    asset_model_id: "model-4",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-23",
    category_id: "icat-2",
    asset_model_id: "model-6",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-24",
    category_id: "icat-2",
    asset_model_id: "model-9",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-30",
    category_id: "icat-3",
    asset_model_id: "model-1",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-31",
    category_id: "icat-3",
    asset_model_id: "model-2",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-32",
    category_id: "icat-3",
    asset_model_id: "model-4",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-40",
    category_id: "icat-4",
    asset_model_id: "model-1",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-41",
    category_id: "icat-4",
    asset_model_id: "model-2",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-42",
    category_id: "icat-4",
    asset_model_id: "model-3",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-43",
    category_id: "icat-4",
    asset_model_id: "model-4",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-44",
    category_id: "icat-4",
    asset_model_id: "model-5",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-50",
    category_id: "icat-5",
    asset_model_id: "model-1",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-51",
    category_id: "icat-5",
    asset_model_id: "model-2",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-52",
    category_id: "icat-5",
    asset_model_id: "model-4",
    createdAt: new Date().toISOString(),
  },
  {
    id: "icam-53",
    category_id: "icat-5",
    asset_model_id: "model-6",
    createdAt: new Date().toISOString(),
  },
];
